import { call, put, select, takeEvery } from 'redux-saga/effects';

import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import * as authSelectors from '../../../../../redux/reducers/auth';
import API_BASE_URL from '../../../../../redux/sagas/settings/apibaseurl';
import * as alerts from '../../../../../redux/utils/alerts';
import * as entitySelectors from '../../../Entities/reducers';
import * as actions from '../../actions/routes';
import * as selectors from '../../reducers/routes';
import * as schemasRouteCheckpoints from '../../schemas/routeCheckpoints';
import * as schemas from '../../schemas/routes';
import * as types from '../../types/routes';

import { store } from '@redux/storeConfig/store';
import { appIntl } from '../../../../../utility/context/IntlGlobalProvider';
import {
  deleteRouteService,
  deleteRoutesService,
  getRoutesListService,
  saveRouteService
} from '../../services/routes';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchRoutesList(action) {
  const { operatorId: filterOperatorId, organizationId: filterOrganizationId } = action.payload;
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  getRoutesListService(
    { operatorId, organizationId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { routes },
          result //order
        } = normalize(resultData, schemas.routes); //normalize data to byId and order
        store.dispatch(actions.completeFetchingRoutes(routes, result, operatorId, organizationId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingRoutes(error));
      }
    }
  );
}

export function* watchFetchRoutesList() {
  yield takeEvery(types.ROUTES_FETCH_STARTED, fetchRoutesList);
}

/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchRoutesList(action) {
  const {
    entityTypeId,
    operatorId: filterOperatorId,
    organizationId: filterOrganizationId
  } = action.payload;
  const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  getRoutesListService(
    { userFetchDate: date, operatorId, organizationId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { routes },
          result //order
        } = normalize(resultData, schemas.routes); //normalize data to byId and order
        store.dispatch(
          actions.completePartialFetchingRoutes(routes, result, operatorId, organizationId)
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingRoutes(error));
      }
    }
  );
}

export function* watchPartialFetchRoutesList() {
  yield takeEvery(types.ROUTES_PARTIAL_FETCH_STARTED, partialFetchRoutesList);
}
/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addRoute(action) {
  const route = action.payload;

  saveRouteService(
    { ...route, route: { ...route.route, routeId: null } },
    {
      successAction: async (response) => {
        const addedRoute = camelcaseKeys(response.data.routeData);
        const routeGroupId = response.data.routeGroupId;
        const pathList = response.data.pathList ? response.data.pathList.map((result) => camelcaseKeys(result)) : [];
        const addedCheckpoints = response.data.checkpointList
          .map((result) => camelcaseKeys(result))
          .map((chk) => {
            const checkpointPaths = pathList
              .filter((path) => path.checkpointId === chk.checkpointId)
              .sort((a, b) => a.orderLatLng - b.orderLatLng)
              .map((path) => ({
                routePathId: path.routePathId,
                checkpointId: path.checkpointId,
                latitude: path.latitude,
                longitude: path.longitude,
                distance: path.distance
              }));
            
            return {
              ...chk,
              segmentPath: checkpointPaths
            };
          });
        const {
          entities: { routeCheckpoints },
          result //order
        } = normalize(addedCheckpoints, schemasRouteCheckpoints.routeCheckpoints); //normalize data to byId and order
        store.dispatch(
          actions.completeAddingRoute(route.route.routeId, {
            route: { ...addedRoute, routeGroupId, pathList }, // AGREGADO: Incluir pathList en la ruta
            checkpoints: { entities: routeCheckpoints, order: result }
          })
        );
      },
      errorAction: async (error) => {
        store.dispatch(actions.failAddingRoute(route.route.routeId, error));
      }
    },
    true
  );
}

export function* watchAddRoutesStarted() {
  yield takeEvery(types.ROUTE_ADD_STARTED, addRoute);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editRoute(action) {
  const route = action.payload;
  const { oldRoute } = yield select(selectors.getRoute, route.route.routeId);

  saveRouteService(
    route,
    {
      successAction: async (response) => {
        const editedRoute = camelcaseKeys(response.data.routeData);
        const routeGroupId = response.data.routeGroupId;
        // AGREGADO: Procesar pathList del Result Set 4
        const pathList = response.data.pathList ? response.data.pathList.map((result) => camelcaseKeys(result)) : [];
        
        // MEJORADO: Construir segmentPath para cada checkpoint usando pathList
        const editedCheckpoints = response.data.checkpointList
          .map((result) => camelcaseKeys(result))
          .map((chk) => {
            // Buscar los paths que corresponden a este checkpoint
            const checkpointPaths = pathList
              .filter((path) => path.checkpointId === chk.checkpointId)
              .sort((a, b) => a.orderLatLng - b.orderLatLng)
              .map((path) => ({
                routePathId: path.routePathId,
                checkpointId: path.checkpointId,
                latitude: path.latitude,
                longitude: path.longitude,
                distance: path.distance
              }));
            
            return {
              ...chk,
              segmentPath: checkpointPaths
            };
          });
        // Computar hasIncompleteCheckpoints desde los checkpoints del response
        // para que el reducer actualice el estado y la alerta desaparezca en el listado
        const hasIncompleteCheckpoints = editedCheckpoints.some((chk) => chk.isIncomplete === 1) ? 1 : 0;

        const {
          entities: { routeCheckpoints },
          result //order
        } = normalize(editedCheckpoints, schemasRouteCheckpoints.routeCheckpoints); //normalize data to byId and order
        store.dispatch(
          actions.completeEditingRoute({
            route: { ...editedRoute, routeGroupId, pathList, hasIncompleteCheckpoints },
            checkpoints: { entities: routeCheckpoints, order: result }
          })
        );
      },
      errorAction: async (error) => {
        store.dispatch(actions.failEditingRoute(oldRoute, error));
      }
    },
    false
  );
}

export function* watchEditRoutesStarted() {
  yield takeEvery(types.ROUTE_EDIT_STARTED, editRoute);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeRoute(action) {
  const { routeId } = action.payload;
  const routeDeleted = yield select(selectors.getRoute, routeId);

  deleteRouteService(
    { routeId, routeName: routeDeleted?.routeName },
    {
      successAction: async (response) => {
        store.dispatch(actions.completeRemovingRoute(routeId));
      },
      errorAction: async (error) => {
        store.dispatch(actions.failRemovingRoute(routeId, error));
      }
    }
  );
}

export function* watchRemoveRouteStarted() {
  yield takeEvery(types.ROUTE_REMOVE_STARTED, removeRoute);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeRoutes(action) {
  const { routeIds } = action.payload;

  deleteRoutesService(
    { routeIds },
    {
      successAction: async (response) => {
        store.dispatch(
          actions.completeRemovingRoutes(routeIds.split(',').map((routeId) => parseInt(routeId)))
        );
      },
      errorAction: async (error) => {
        store.dispatch(
          actions.failRemovingRoutes(
            routeIds.split(',').map((routeId) => parseInt(routeId)),
            error
          )
        );
      }
    }
  );
}

export function* watchRemoveRoutesStarted() {
  yield takeEvery(types.ROUTES_REMOVE_STARTED, removeRoutes);
}

/* -------------------------------------------------------------------------- */
/*                                  Copy Route                                */
/* -------------------------------------------------------------------------- */
function* copyRoute(action) {
  const route = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/route/copyRoute`, {
        method: 'POST',
        body: JSON.stringify(route),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(
          actions.completeCopyRoute(
            route.routeId,
            jsonResult.data.newRouteId,
            route.newRouteName,
            route.organizationId,
            route.divisionId,
            route.subdivisionId
          )
        );

        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.copySuccessful'
          })
        });
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.copyError'
          }),
          errorNumber: jsonResult.number
        };
        alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failCopyRoute(error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: appIntl().formatMessage({
        id: 'routes.copyError'
      }),
      errorNumber: -1
    };
    alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failCopyRoute(error));
  }
}

export function* watchCopyRouteStarted() {
  yield takeEvery(types.ROUTE_COPY_STARTED, copyRoute);
}
